import { useState, useMemo, useCallback } from 'react';
import type { IDevExTeamMappingRule, IDevExTeamUnmatchedRow, ITeam } from '@skystone/shared';
import type { ColumnDef } from '@/components/ui';
import {
  Badge,
  Button,
  DataTable,
  Dialog,
  DialogFooter,
  ErrorBanner,
  PageHeader,
  PageLoading,
} from '@/components/ui';
import { useApi } from '@/hooks/useApi';
import { devExApi } from '@/services/devex-api';
import { teamApi } from '@/services/team-api';
import { renderOptionalStringCell } from '@/lib/table-cell-renderers';
import { getErrorMessage } from '@/lib/utils';
import { inputClass } from '@/lib/form-styles';
import { PageContainer } from '@/components/layout';

// ── Types ─────────────────────────────────────────────────────────────────────

type DisplayRuleType = IDevExTeamMappingRule['ruleType'] | 'unmatched';
type DisplayStatus = IDevExTeamMappingRule['status'] | 'unknown';
type BadgeVariant = 'default' | 'secondary' | 'success' | 'warning' | 'error';

interface IDisplayRow {
  id: string;
  sourceTeamValue: string;
  ruleType: DisplayRuleType;
  targetTeamName: string | null;
  targetProductName: string | null;
  status: DisplayStatus;
  unmatchedRowId: string | null;
}

interface IMapDialogState {
  open: boolean;
  row: IDisplayRow | null;
  selectedTeamId: string;
  saving: boolean;
  error: string | null;
}

interface IRemoveDialogState {
  open: boolean;
  row: IDisplayRow | null;
  removing: boolean;
  error: string | null;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const RULE_TYPE_VARIANT: Record<DisplayRuleType, BadgeVariant> = {
  exact: 'secondary',
  normalized_alias: 'default',
  unmatched: 'error',
};

const RULE_TYPE_LABEL: Record<DisplayRuleType, string> = {
  exact: 'Exact',
  normalized_alias: 'Alias',
  unmatched: 'Unmatched',
};

const STATUS_VARIANT: Record<DisplayStatus, BadgeVariant> = {
  active: 'success',
  inactive: 'warning',
  unknown: 'secondary',
};

const CLOSED_DIALOG: IMapDialogState = {
  open: false,
  row: null,
  selectedTeamId: '',
  saving: false,
  error: null,
};

const CLOSED_REMOVE_DIALOG: IRemoveDialogState = {
  open: false,
  row: null,
  removing: false,
  error: null,
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function deduplicateByTeamRaw(rows: IDevExTeamUnmatchedRow[]): IDevExTeamUnmatchedRow[] {
  const seen = new Set<string>();
  return rows.filter((row) => {
    if (!row.teamRaw) return false;
    if (seen.has(row.teamRaw)) return false;
    seen.add(row.teamRaw);
    return true;
  });
}

function buildDisplayRows(
  rules: IDevExTeamMappingRule[],
  unmatched: IDevExTeamUnmatchedRow[],
): IDisplayRow[] {
  const ruleRows: IDisplayRow[] = rules.map((r) => ({
    id: r.id,
    sourceTeamValue: r.sourceTeamValue,
    ruleType: r.ruleType,
    targetTeamName: r.targetTeamName,
    targetProductName: r.targetProductName,
    status: r.status,
    unmatchedRowId: null,
  }));

  const unmatchedRows: IDisplayRow[] = deduplicateByTeamRaw(unmatched).map((u) => ({
    id: `unmatched-${u.id}`,
    sourceTeamValue: u.teamRaw ?? '',
    ruleType: 'unmatched' as const,
    targetTeamName: null,
    targetProductName: null,
    status: 'unknown' as const,
    unmatchedRowId: u.id,
  }));

  return [...ruleRows, ...unmatchedRows];
}

// ── Column factory ────────────────────────────────────────────────────────────

function createColumns(
  onMap: (row: IDisplayRow) => void,
  onRemove: (row: IDisplayRow) => void,
): ColumnDef<IDisplayRow>[] {
  return [
    {
      id: 'sourceTeamValue',
      header: 'Survey Team Name',
      accessor: 'sourceTeamValue',
      sortable: true,
      filter: { type: 'text', placeholder: 'Filter survey name...' },
      cell: (value) => <span className="font-mono text-sm">{value as string}</span>,
    },
    {
      id: 'ruleType',
      header: 'Rule Type',
      accessor: 'ruleType',
      filter: { type: 'text', placeholder: 'Filter rule type...' },
      cell: (value) => {
        const ruleType = value as DisplayRuleType;
        return <Badge variant={RULE_TYPE_VARIANT[ruleType]}>{RULE_TYPE_LABEL[ruleType]}</Badge>;
      },
    },
    {
      id: 'targetTeamName',
      header: 'Target Team',
      accessor: 'targetTeamName',
      sortable: true,
      filter: { type: 'text', placeholder: 'Filter team...' },
      cell: renderOptionalStringCell,
    },
    {
      id: 'targetProductName',
      header: 'Product',
      accessor: 'targetProductName',
      sortable: true,
      cell: renderOptionalStringCell,
    },
    {
      id: 'status',
      header: 'Status',
      accessor: 'status',
      filter: { type: 'text', placeholder: 'Filter status...' },
      cell: (value) => {
        const status = value as DisplayStatus;
        return <Badge variant={STATUS_VARIANT[status]}>{status}</Badge>;
      },
    },
    {
      id: 'actions',
      header: '',
      accessor: 'unmatchedRowId',
      cell: (_value, row) => {
        if (row.unmatchedRowId) {
          return (
            <Button size="sm" variant="outline" onClick={() => onMap(row)}>
              Map
            </Button>
          );
        }
        return (
          <Button size="sm" variant="ghost" onClick={() => onRemove(row)}>
            Remove
          </Button>
        );
      },
    },
  ];
}

// ── Component ─────────────────────────────────────────────────────────────────

export function DevExTeamMappingPage() {
  const {
    data: rules,
    loading: rulesLoading,
    error: rulesError,
    refetch: refetchRules,
  } = useApi(() => devExApi.listMappingRules(), []);

  const {
    data: unmatched,
    loading: unmatchedLoading,
    error: unmatchedError,
    refetch: refetchUnmatched,
  } = useApi(() => devExApi.listMappingUnmatched(), []);

  const { data: teams } = useApi(() => teamApi.getAll({ includeArchived: false }), []);

  const [dialog, setDialog] = useState<IMapDialogState>(CLOSED_DIALOG);
  const [removeDialog, setRemoveDialog] = useState<IRemoveDialogState>(CLOSED_REMOVE_DIALOG);

  const displayRows = useMemo(
    () => buildDisplayRows(rules ?? [], unmatched ?? []),
    [rules, unmatched],
  );

  const handleOpenMap = useCallback((row: IDisplayRow): void => {
    setDialog({ open: true, row, selectedTeamId: '', saving: false, error: null });
  }, []);

  const handleOpenRemove = useCallback((row: IDisplayRow): void => {
    setRemoveDialog({ open: true, row, removing: false, error: null });
  }, []);

  const columns = useMemo(() => createColumns(handleOpenMap, handleOpenRemove), [handleOpenMap, handleOpenRemove]);

  const handleSubmitMap = async (): Promise<void> => {
    if (!dialog.row || !dialog.selectedTeamId) return;
    setDialog((prev) => ({ ...prev, saving: true, error: null }));
    try {
      await devExApi.upsertMappingRule({
        ruleType: 'normalized_alias',
        sourceTeamValue: dialog.row.sourceTeamValue,
        targetTeamId: dialog.selectedTeamId,
      });
      setDialog(CLOSED_DIALOG);
      refetchRules();
      refetchUnmatched();
    } catch (err: unknown) {
      setDialog((prev) => ({ ...prev, saving: false, error: getErrorMessage(err, 'Failed to create mapping rule') }));
    }
  };

  const handleConfirmRemove = async (): Promise<void> => {
    if (!removeDialog.row) return;
    setRemoveDialog((prev) => ({ ...prev, removing: true, error: null }));
    try {
      await devExApi.deleteMappingRule(removeDialog.row.id);
      setRemoveDialog(CLOSED_REMOVE_DIALOG);
      refetchRules();
    } catch (err: unknown) {
      setRemoveDialog((prev) => ({ ...prev, removing: false, error: getErrorMessage(err, 'Failed to remove mapping rule') }));
    }
  };

  if (rulesLoading || unmatchedLoading) return <PageLoading />;

  return (
    <PageContainer variant="wide" className="space-y-6">
      <PageHeader
        title="DevEx - Survey Team Mapping"
        subtitle="All rules that resolve DevEx survey team name strings to system teams during quarterly data ingestion."
      />

      <ErrorBanner message={rulesError ?? unmatchedError} />

      <DataTable
        data={displayRows}
        columns={columns}
        keyExtractor={(row) => row.id}
        enablePagination
        enableFilters
        emptyMessage="No mapping rules found."
      />

      <Dialog open={removeDialog.open} onClose={() => setRemoveDialog(CLOSED_REMOVE_DIALOG)} title="Remove Mapping Rule">
        <ErrorBanner message={removeDialog.error} className="mb-4" />
        <p className="text-sm text-muted-foreground mb-4">
          Remove the mapping rule for{' '}
          <span className="font-mono font-medium text-foreground">{removeDialog.row?.sourceTeamValue}</span>?
          This will unlink any quarterly data rows that used this rule.
        </p>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setRemoveDialog(CLOSED_REMOVE_DIALOG)} disabled={removeDialog.removing}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirmRemove}
            disabled={removeDialog.removing}
            isLoading={removeDialog.removing}
          >
            Remove
          </Button>
        </DialogFooter>
      </Dialog>

      <Dialog open={dialog.open} onClose={() => setDialog(CLOSED_DIALOG)} title="Map Survey Team Name">
        <ErrorBanner message={dialog.error} className="mb-4" />
        <p className="text-sm text-muted-foreground mb-4">
          Create a mapping rule for{' '}
          <span className="font-mono font-medium text-foreground">{dialog.row?.sourceTeamValue}</span>.
        </p>
        <div className="space-y-2">
          <label className="text-sm font-medium" htmlFor="map-target-team">
            Target Team
          </label>
          <select
            id="map-target-team"
            className={inputClass}
            value={dialog.selectedTeamId}
            onChange={(e) => setDialog((prev) => ({ ...prev, selectedTeamId: e.target.value }))}
          >
            <option value="">Select a team...</option>
            {(teams ?? []).map((team: ITeam) => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </select>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setDialog(CLOSED_DIALOG)} disabled={dialog.saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitMap}
            disabled={!dialog.selectedTeamId || dialog.saving}
            isLoading={dialog.saving}
          >
            Create Rule
          </Button>
        </DialogFooter>
      </Dialog>
    </PageContainer>
  );
}
